/**
 * Utility functions for ClientDashboard
 */
import Highcharts from "highcharts/highstock";

// Set global Highcharts number formatting
// so large values display with comma separators
Highcharts.setOptions({
  lang: {
    thousandsSep: ",",
  },
});

// Common react-select option shape used in dashboard filters
export type SelectOption = { label: string; value: string };

// Frontend filter shape used inside dashboard component state
export type DashboardFilters = {
  businessUnits?: string[];
  offices?: string[];
  partners?: string[];
  fiscalYears?: string[];
};

// Backend payload shape expected by API endpoints
export type DashboardFiltersPayload = {
  BusinessUnit?: string[];
  Office?: string[];
  PrimaryPartner?: string[];
  FiscalYear?: string[];
};

// Standard API response wrapper
export type ApiContext<T> = {
  success?: boolean;
  message?: string;
  data?: T;
  errors?: Record<string, string>;
};

// Approval status row used for KPI counts and status-based summaries
export type ApprovalStatusRow = {
  ApprovalStatus: string;
  TotalCount: number;
};

// Office-level grouped chart row
export type OfficeStatusRow = {
  Office: string;
  ApprovalStatus: string;
  TotalCount: number;
};

// Partner-level grouped chart row
export type PartnerStatusRow = {
  PrimaryPartner: string;
  ApprovalStatus: string;
  TotalCount: number;
};


// Executive view
export type ClientPracticeSummaryRow = {
  TotalClientsInPractice: number;
  TotalForecastAmount: number;
};

export type LineOfServiceSummaryRow = {
  LineOfService: string;
  ClientCount: number;
  ForecastAmount: number;
};

export type ClientTypeSummaryRow = {
  ClientType: string;
  ClientCount: number;
  ForecastAmount: number;
};

export type PrimaryPartnerClientTypeRow = {
  PrimaryPartner: string;
  ClientType: string;
  ClientCount: number;
  ForecastAmount: number;
  TotalPrimaryPartnerCount?: number;
  TotalPrimaryPartnerAmount?: number;
};

export type OfficeClientTypeRow = {
  Office: string;
  ClientType: string;
  ClientCount: number;
  ForecastAmount: number;
  TotalOfficeCount?: number;
  TotalOfficeAmount?: number;
};

// Optional type for dropdown data coming from global store
export type DropdownStoreState = {
  purposeOptions?: Array<{ label: string; value: string; code?: string }>;
  officeLocationOptions?: Array<{ label: string; value: string; id?: string }>;
  primaryPartnerOptions?: Array<{ label: string; value: string; id?: string; location?: string }>;
};

// Theme colors used for chart styling where needed
export type ThemeColors = {
  primary: string;
  tertiary: string;
  palette: string[];
};

// Shared "All" option for dashboard filters
export const ALL_OPTION: SelectOption = { label: "All", value: "All" };

// Fixed color mapping for known client approval statuses
export const STATUS_COLOR_MAP: Record<string, string> = {
  Approved: "#388E3C",
  "Pending Approval": "#1976D2",
  Rejected: "#D32F2F",
  Prospect: "#FFD54F",
};

// Fixed display order for approval statuses across charts
export const FIXED_APPROVAL_STATUS_ORDER = [
  "Prospect",
  "Pending Approval",
  "Rejected",
  "Approved",
] as const;

// Convert frontend filter object into backend payload format
// Only include keys when values are present
export const buildFiltersPayload = (
  filters: DashboardFilters
): DashboardFiltersPayload => {
  const payload: DashboardFiltersPayload = {};

  if (filters.businessUnits?.length) payload.BusinessUnit = filters.businessUnits;
  if (filters.offices?.length) payload.Office = filters.offices;
  if (filters.partners?.length) payload.PrimaryPartner = filters.partners;
  if (filters.fiscalYears?.length) payload.FiscalYear = filters.fiscalYears;

  return payload;
};

// Returns current tax year in FY format used by the dashboard
export const getCurrentTaxYearValue = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  return `FY${year}`;
};

// Safe number helper to avoid NaN / invalid chart values
export const safeNumber = (n: unknown): number =>
  typeof n === "number" && Number.isFinite(n) ? n : 0;

export const safeCurrencyNumber = (n: unknown): number => {
  if (typeof n === "number" && Number.isFinite(n)) return n;

  const parsed = Number(
    String(n ?? "")
      .replace(/\$/g, "")
      .replace(/,/g, "")
      .trim()
  );

  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatCompactCurrency = (value: number): string => {
  const amount = safeCurrencyNumber(value);

  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(2)}M`;
  }

  if (amount >= 100_000) {
    return `${(amount / 1_000).toFixed(2)}K`;
  }

  return Highcharts.numberFormat(amount, 0);
};

export const formatChartCurrencyCompact = (value: number): string => {
  const amount = safeCurrencyNumber(value);

  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(2)} M`;
  }

  if (amount >= 10_000) {
    return `$${(amount / 1_000).toFixed(1)} K`;
  }

  return `$${Highcharts.numberFormat(amount, 0)}`;
};

export const getThemeColor = (
  palette: string[] | undefined,
  index: number,
  fallback = "#7f7f7f"
): string => {
  if (!palette || palette.length === 0) return fallback;
  return palette[index % palette.length] || fallback;
};

export const buildStablePaletteMap = (
  keys: string[],
  palette: string[] | undefined,
  fallback = "#7f7f7f"
): Record<string, string> => {
  const uniqueKeys = Array.from(new Set(keys.filter(Boolean))).sort();

  return uniqueKeys.reduce<Record<string, string>>((acc, key, index) => {
    acc[key] =
      palette && palette.length > 0
        ? palette[index % palette.length] || fallback
        : fallback;
    return acc;
  }, {});
};

export const getPersistentPaletteMap = (
  storageKey: string,
  keys: string[],
  palette: string[] | undefined,
  fallback = "#7f7f7f"
): Record<string, string> => {
  if (!palette || palette.length === 0) {
    return keys.reduce<Record<string, string>>((acc, key) => {
      acc[key] = fallback;
      return acc;
    }, {});
  }

  let savedMap: Record<string, string> = {};

  try {
    const raw = localStorage.getItem(storageKey);
    savedMap = raw ? JSON.parse(raw) : {};
  } catch {
    savedMap = {};
  }

  const uniqueKeys = Array.from(new Set(keys.filter(Boolean)));

  uniqueKeys.forEach((key) => {
    if (!savedMap[key]) {
      const usedColors = new Set(Object.values(savedMap));
      const nextColor =
        palette.find((c) => !usedColors.has(c)) ||
        palette[Object.keys(savedMap).length % palette.length] ||
        fallback;

      savedMap[key] = nextColor;
    }
  });

  try {
    localStorage.setItem(storageKey, JSON.stringify(savedMap));
  } catch {
    // no-op
  }

  return savedMap;
};

// Shared Highcharts defaults for empty-state handling
export const chartDefaults: Highcharts.Options = {
  lang: { noData: "No data available" },
  noData: {
    style: {
      fontWeight: "bold",
      fontSize: "16px",
      color: "#6E6E6E",
    },
  },
};

type ColumnSeries = Highcharts.SeriesColumnOptions;
type BarSeries = Highcharts.SeriesBarOptions;
type PieSeries = Highcharts.SeriesPieOptions;

// Design Setting for Scroll bar-button
type ScrollbarOptions = {
  enabled: boolean;
  height?: number;
  margin?: number;
  minWidth?: number;
};

const buildStandardScrollbar = ({
  enabled,
  height = 10,
  margin = 8,
  minWidth = 30,
}: ScrollbarOptions): any => ({
  enabled,
  showFull: false,

  // thumb
  barBorderRadius: 6,
  barBorderWidth: 0,
  barBackgroundColor: "#6B7280",
  barBorderColor: "#6B7280",
  rifleColor: "#F9FAFB",

  // track
  trackBorderRadius: 6,
  trackBorderWidth: 0,
  trackBackgroundColor: "#E5E7EB",
  trackBorderColor: "#E5E7EB",

  // buttons
  buttonBorderWidth: 0,
  buttonArrowColor: "#4B5563",
  buttonBackgroundColor: "#D1D5DB",
  buttonBorderColor: "#D1D5DB",

  height,
  margin,
  minWidth,
  mouseWheelEnabled: true,
});

type BindWheelScrollOptions = {
  chart: Highcharts.Chart;
  totalItems: number;
  visibleCountDefault?: number;
  bindingKey: string;
};

const bindCategoryAxisWheelScroll = ({
  chart,
  totalItems,
  visibleCountDefault = 4,
  bindingKey,
}: BindWheelScrollOptions) => {
  const axis = chart.xAxis[0];
  const container = chart.container as any;

  if (container[bindingKey]) return;
  container[bindingKey] = true;

  container.addEventListener(
    "wheel",
    function (e: WheelEvent) {
      if (totalItems <= visibleCountDefault + 1) return;

      e.preventDefault();

      const currentMin = Math.max(0, Math.floor(Number(axis.min ?? 0)));
      const currentMax = Math.min(
        totalItems - 1,
        Math.ceil(Number(axis.max ?? visibleCountDefault))
      );

      const visibleCount = currentMax - currentMin + 1;
      const step = 1;

      let nextMin = currentMin;

      if (e.deltaY > 0) {
        nextMin = Math.min(totalItems - visibleCount, currentMin + step);
      } else {
        nextMin = Math.max(0, currentMin - step);
      }

      const nextMax = nextMin + visibleCount - 1;
      axis.setExtremes(nextMin, nextMax, true, false);
    },
    { passive: false }
  );
};

// Builds stacked column chart for Clients by Office
// Each stack segment represents approval status count
export const buildClientOfficeStackedColumnOptions = (
  rows: OfficeStatusRow[],
  statuses: readonly string[] = FIXED_APPROVAL_STATUS_ORDER
): Highcharts.Options => {
  // Get unique offices from response rows
  const offices = Array.from(new Set(rows.map((r) => r.Office))).filter(Boolean);

  // Sort offices by total client count descending
  const totals = offices
    .map((office) => ({
      office,
      total: rows
        .filter((r) => r.Office === office)
        .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0),
    }))
    .sort((a, b) => b.total - a.total)
    .map((x) => x.office);

  // Helper to get count for one office + one status
  const getVal = (office: string, status: string) =>
    rows
      .filter((r) => r.Office === office && r.ApprovalStatus === status)
      .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

  // Build one series per approval status
  const series: ColumnSeries[] = [...statuses].map((status) => ({
    type: "column",
    name: status,
    data: totals.map((office) => getVal(office, status)),
    color: STATUS_COLOR_MAP[status] || undefined,
  }));

  return {
    ...chartDefaults,
    chart: {
      type: "column",
      spacingBottom: 20,
      marginRight: 60,
      events: {
        load: function () {
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: totals.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundOffice",
          });
        },
      },
    },
    title: { text: "Prospect Clients by Office" },
    xAxis: {
      categories: totals,
      gridLineWidth: 0,
      title: { text: "Office" },
      labels: { style: { fontSize: "12px" } },
      min: 0,
      max: Math.min(4, Math.max(totals.length - 1, 0)),
      scrollbar: buildStandardScrollbar({
        enabled: totals.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
    },
    yAxis: {
      min: 0,
      gridLineWidth: 1,
      title: { text: "Total Count" },
      allowDecimals: false,
      stackLabels: { enabled: true },
    },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const category = String(this.x ?? "");
          const status = String(this.series.name ?? "");
          const count = Number(this.y ?? 0);
          const total = Number(point?.stackTotal ?? 0);

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${category}</div>
              <div>${status}: <b>${count}</b></div>
              <div>Total: <b>${total}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 28,
        pointPadding: 0.18,
        groupPadding: 0.2,
        borderWidth: 1,
        borderColor: "#ffffff",
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
    },
    series,
  } satisfies Highcharts.Options;
};


// Builds stacked horizontal bar chart for Clients by Primary Partner
// Each stack segment represents approval status count
export const buildClientPartnerStackedBarOptions = (
  rows: PartnerStatusRow[],
  statuses: readonly string[] = FIXED_APPROVAL_STATUS_ORDER
): Highcharts.Options => {
  const partners = Array.from(new Set(rows.map((r) => r.PrimaryPartner))).filter(Boolean);

  // sort by totals desc
  const totals = partners
    .map((p) => ({
      p,
      total: rows
        .filter((r) => r.PrimaryPartner === p)
        .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0),
    }))
    .sort((a, b) => b.total - a.total)
    .map((x) => x.p);

  const getVal = (partner: string, status: string) =>
    rows
      .filter((r) => r.PrimaryPartner === partner && r.ApprovalStatus === status)
      .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

  const series: BarSeries[] = [...statuses].map((status) => ({
    type: "bar",
    name: status,
    data: totals.map((partner) => getVal(partner, status)),
    color: STATUS_COLOR_MAP[status] || undefined,
  }));

  return {
    ...chartDefaults,
    chart: {
      type: "bar",
      marginRight: 50,
      spacingBottom: 20,
      events: {
        load: function () {
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: totals.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundPartner",
          });
        },
      },
    },
    title: { text: "Prospect Clients by Primary Partner" },
    xAxis: {
      categories: totals,
      title: { text: "Primary Partner" },
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      min: 0,
      max: Math.min(4, Math.max(totals.length - 1, 0)),
      scrollbar: buildStandardScrollbar({
        enabled: totals.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
    },
    yAxis: {
      min: 0,
      endOnTick: false,
      maxPadding: 0.12,
      gridLineWidth: 1,
      title: { text: "Total Count" },
      stackLabels: {
        enabled: true,
        crop: false,
      },
    },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const category = String(this.x ?? "");
          const status = String(this.series.name ?? "");
          const count = Number(this.y ?? 0);
          const total = Number(point?.stackTotal ?? 0);

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${category}</div>
              <div>${status}: <b>${count}</b></div>
              <div>Total: <b>${total}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      bar: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 26,
        groupPadding: 0.12,
        pointPadding: 0.04,
        borderWidth: 1,
        borderColor: "#ffffff",
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      margin: 12,
      padding: 8,
    },
    series,
  } satisfies Highcharts.Options;
};


// Builds executive LOS pie chart
// Pie slice size = forecast amount
// Tooltip also shows client count
export const buildClientsInPracticeLineOfServicePieOptions = (
  rows: LineOfServiceSummaryRow[],
  theme?: ThemeColors
): Highcharts.Options => {
  const normalized = rows
    .map((r) => ({
      LineOfService: String(r.LineOfService || "No Line of Service")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      ClientCount: safeNumber(r.ClientCount),
      ForecastAmount: safeCurrencyNumber(r.ForecastAmount),
    }))
    .filter((r) => r.ForecastAmount > 0 || r.ClientCount > 0)
    .sort((a, b) => b.ForecastAmount - a.ForecastAmount);

  const losKeys = normalized.map((r) => r.LineOfService);

  const colorMap = getPersistentPaletteMap(
    "client-dashboard.line-of-service.colors",
    losKeys,
    theme?.palette
  );

  const totalForecast = normalized.reduce(
    (sum, r) => sum + safeCurrencyNumber(r.ForecastAmount),
    0
  );

  const minimumVisibleShare = 0.01;

  const series: PieSeries[] = [
    {
      type: "pie",
      name: "Revenue Amount",
      data: normalized.map((r) => {
        const actualAmount = safeCurrencyNumber(r.ForecastAmount);
        const actualShare = totalForecast > 0 ? actualAmount / totalForecast : 0;

        return {
          name: r.LineOfService,
          y:
            actualAmount > 0 && actualShare < minimumVisibleShare
              ? totalForecast * minimumVisibleShare
              : actualAmount,
          color: colorMap[r.LineOfService],
          custom: {
            actualAmount,
            clientCount: r.ClientCount,
            actualShare,
          },
        };
      }),
    },
  ];

  return {
    ...chartDefaults,
    chart: { type: "pie" },
    title: { text: "Expected Revenue by Line of Service" },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const lineOfService = String(point?.name ?? "");
          const amount = Number(
            point?.custom?.actualAmount ?? point?.y ?? 0
          );
          const clientCount = Number(
            point?.custom?.clientCount ?? 0
          );
          const revenueShare = Number(
            point?.custom?.actualShare != null
              ? point.custom.actualShare * 100
              : point?.percentage ?? 0
          );

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${lineOfService}</div>
              <div>Revenue: <b>${formatChartCurrencyCompact(amount)}</b></div>
              <div>Revenue Share: <b>${Highcharts.numberFormat(revenueShare, 1)}%</b></div>
              <div>Clients: <b>${clientCount}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        size: "80%",
        dataLabels: {
          enabled: true,
          formatter: function () {
            const point = this.point as any;
            return `<b>${point.name}</b>: ${formatChartCurrencyCompact(Number(point.y ?? 0))}`;
          },
        },
        showInLegend: true,
      },
    },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    series,
  } satisfies Highcharts.Options;
};


// Builds executive Client Type combo chart
// Column = client count
// Line = forecast amount
export const buildClientsInPracticeClientTypeComboOptions = (
  rows: ClientTypeSummaryRow[],
  theme?: ThemeColors
): Highcharts.Options => {
  // Normalize and sort rows
  const normalized = rows
    .map((r) => ({
      ClientType: String(r.ClientType || "No Client Type"),
      ClientCount: safeNumber(r.ClientCount),
      ForecastAmount: safeCurrencyNumber(r.ForecastAmount),
    }))
    .sort((a, b) => b.ForecastAmount - a.ForecastAmount);

  // Create stable theme-based colors
  const clientTypeKeys = normalized.map((r) => r.ClientType);

  const colorMap = getPersistentPaletteMap(
    "client-dashboard.shared-client-type.colors",
    clientTypeKeys,
    theme?.palette
  );

  // Return Highcharts combo config
  return {
    ...chartDefaults,
    chart: {
      type: "column",
      spacingBottom: 20,
      events: {
        load: function () {
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: normalized.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundClientTypeCombo",
          });
        },
      },
    },
    title: { text: "Client Type Summary" },
    xAxis: {
      categories: normalized.map((r) => r.ClientType),
      crosshair: true,
      title: { text: "Client Type" },
      labels: {
        rotation: 0,
        style: { fontSize: "12px" },
      },
      min: 0,
      max: Math.min(4, Math.max(normalized.length - 1, 0)),
      scrollbar: buildStandardScrollbar({
        enabled: normalized.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
    },
    yAxis: [
      {
        title: { text: "Client Count" },
        min: 0,
        allowDecimals: false,
      },
      {
        title: { text: "Revenue Amount ($)" },
        min: 0,
        opposite: true,
        labels: {
          formatter: function () {
            return `$${Highcharts.numberFormat(Number(this.value), 0)}`;
          },
        },
      },
    ],
    tooltip: {
      shared: true,
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const points = this.points || [];
          const clientType = String(this.x ?? "");

          const countPoint = points.find(
            (p) => p.series.name === "Client Count"
          );

          const amountPoint = points.find(
            (p) => p.series.name === "Revenue Amount"
          );

          const count = Number(countPoint?.y ?? 0);
          const amount = Number(amountPoint?.y ?? 0);

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${clientType}</div>
              <div>Revenue: <b>${formatChartCurrencyCompact(amount)}</b></div>
              <div>Clients: <b>${count}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
    },
    plotOptions: {
      column: {
        pointWidth: 34,
      },
      line: {
        marker: {
          enabled: true,
        },
      },
    },
    series: [
      {
        type: "column",
        name: "Client Count",
        yAxis: 0,
        data: normalized.map((r) => ({
          y: r.ClientCount,
          color: colorMap[r.ClientType],
        })),
      },
      {
        type: "line",
        name: "Revenue Amount",
        yAxis: 1,
        color: "#000000",
        marker: {
          enabled: true,
          fillColor: "#000000",
          lineColor: "#000000",
          lineWidth: 1.5,
          radius: 4,
        },
        data: normalized.map((r) => ({
          y: r.ForecastAmount,
          color: "#000000",
          marker: {
            fillColor: colorMap[r.ClientType],
            lineColor: "#000000",
            lineWidth: 1.5,
            radius: 4,
          },
        })),
      },
    ],
  } satisfies Highcharts.Options;
};


// Builds executive Primary Partner stacked bar chart
// Stacks = client type
// Tooltip shows count and forecast amount
export const buildClientsInPracticePrimaryPartnerStackedBarOptions = (
  rows: PrimaryPartnerClientTypeRow[],
  theme?: ThemeColors
): Highcharts.Options => {
  const partners = Array.from(new Set(rows.map((r) => r.PrimaryPartner))).filter(Boolean);

  const partnerTotalsMap = partners.reduce<
    Record<string, { count: number; amount: number }>
  >((acc, partner) => {
    const partnerRows = rows.filter((r) => r.PrimaryPartner === partner);
    acc[partner] = {
      count: partnerRows.reduce((sum, r) => sum + safeNumber(r.ClientCount), 0),
      amount: partnerRows.reduce(
        (sum, r) => sum + safeCurrencyNumber(r.ForecastAmount),
        0
      ),
    };
    return acc;
  }, {});

  const totals = partners
    .map((p) => ({
      p,
      totalCount: partnerTotalsMap[p]?.count ?? 0,
      totalAmount: partnerTotalsMap[p]?.amount ?? 0,
    }))
    .sort((a, b) => {
      if (b.totalAmount !== a.totalAmount) return b.totalAmount - a.totalAmount;
      return b.totalCount - a.totalCount;
    })
    .map((x) => x.p);

  const setVisibleYAxisExtremes = (chart: Highcharts.Chart) => {
    try {
      const categoryAxis = chart.xAxis[0];
      const valueAxis = chart.yAxis[0];

      const minIndex = Math.max(0, Math.floor(Number(categoryAxis.min ?? 0)));
      const maxIndex = Math.min(
        Math.max(totals.length - 1, 0),
        Math.ceil(Number(categoryAxis.max ?? totals.length - 1))
      );

      let visibleMax = 0;

      for (let i = minIndex; i <= maxIndex; i++) {
        let stackTotal = 0;

        chart.series.forEach((series: any) => {
          if (series.visible) {
            const point = series.data?.[i];
            stackTotal += Number(point?.y ?? 0);
          }
        });

        visibleMax = Math.max(visibleMax, stackTotal);
      }

      const paddedMax = visibleMax > 0 ? visibleMax * 1.30 : 1;
      valueAxis.setExtremes(0, paddedMax, false, false);
      chart.redraw(false);
    } catch (e) {
      // no-op
    }
  };

  const clientTypes = Array.from(new Set(rows.map((r) => r.ClientType))).filter(Boolean);

  const colorMap = getPersistentPaletteMap(
    "client-dashboard.shared-client-type.colors",
    clientTypes,
    theme?.palette
  );

  const getCount = (partner: string, clientType: string) =>
    rows
      .filter((r) => r.PrimaryPartner === partner && r.ClientType === clientType)
      .reduce((sum, r) => sum + safeNumber(r.ClientCount), 0);

  const getAmount = (partner: string, clientType: string) =>
    rows
      .filter((r) => r.PrimaryPartner === partner && r.ClientType === clientType)
      .reduce((sum, r) => sum + safeCurrencyNumber(r.ForecastAmount), 0);

  const series: BarSeries[] = clientTypes.map((clientType) => ({
    type: "bar",
    name: clientType,
    color: colorMap[clientType],
    data: totals.map((partner) => ({
      y: getAmount(partner, clientType),
      custom: {
        count: getCount(partner, clientType),
        forecastAmount: getAmount(partner, clientType),
        clientType,
        partnerName: partner,
      },
    })),
  }));

  return {
    ...chartDefaults,
    chart: {
      type: "bar",
      marginRight: 50,
      spacingBottom: 20,
      events: {
        load: function () {
          setVisibleYAxisExtremes(this);
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: totals.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundExecutivePartner",
          });
        },
      },
    },
    title: { text: "Expected Revenue by Primary Partner" },
    xAxis: {
      categories: totals,
      title: { text: "Primary Partner" },
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      min: 0,
      max: Math.min(4, Math.max(totals.length - 1, 0)),
      events: {
        afterSetExtremes: function () {
          setVisibleYAxisExtremes(this.chart);
        },
      },
      scrollbar: buildStandardScrollbar({
        enabled: totals.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
    },
    yAxis: {
      min: 0,
      endOnTick: false,
      maxPadding: 0.12,
      title: { text: "Revenue Amount ($)" },
      labels: {
        rotation: 0,
        style: {
          whiteSpace: "nowrap",
          fontSize: "11px",
        },
        formatter: function () {
          return formatChartCurrencyCompact(Number(this.value));
        },
      },
      tickPixelInterval: 90,
      gridLineWidth: 0,
      lineWidth: 0,
      stackLabels: {
        enabled: true,
        useHTML: true,
        x: 2,
        y: 0,
        overflow: "allow",
        crop: false,
        formatter: function () {
          const partner = totals[this.x as number];
          const total = partnerTotalsMap[partner];

          if (!total) {
            return `${formatChartCurrencyCompact(Number(this.total ?? 0))}`;
          }

          return `
            <div style="
              text-align: center;
              background: #ffffff;
              padding: 4px 8px;
              border-radius: 6px;
              min-width: 78px;
            ">
              <div style="font-size: 11px; font-weight: 600; margin-bottom: 2px;">
                ${formatChartCurrencyCompact(Number(this.total ?? 0))}
              </div>
              <div style="font-size: 10px; color: #6b7280;">
                ${safeNumber(total.count)} clients
              </div>
            </div>
          `;
        },
      },
    },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const partner = String(this.key ?? "");
          const amount = Number(this.y ?? 0);
          const count = Number(point?.custom?.count ?? 0);
          const clientType = String(point?.custom?.clientType ?? this.series.name ?? "");

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${partner}</div>
              <div>${clientType} Revenue: <b>${formatChartCurrencyCompact(amount)}</b></div>
              <div>${clientType} Clients: <b>${count}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      bar: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 26,
        groupPadding: 0.22,
        pointPadding: 0.16,
        borderWidth: 1,
        borderColor: "#ffffff",
        events: {
          legendItemClick: function () {
            const chart = this.chart;
            setTimeout(() => setVisibleYAxisExtremes(chart), 0);
          },
        },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      margin: 12,
      padding: 8,
    },
    series,
  } satisfies Highcharts.Options;
};


// Builds executive Office stacked column chart
// Stacks = client type
// Tooltip shows count and forecast amount
export const buildClientsInPracticeOfficeStackedColumnOptions = (
  rows: OfficeClientTypeRow[],
  theme?: ThemeColors
): Highcharts.Options => {
  // Get unique offices
  const offices = Array.from(new Set(rows.map((r) => r.Office))).filter(Boolean);

  // Build total maps for sorting
  const officeTotalsMap = offices.reduce<
    Record<string, { count: number; amount: number }>
  >((acc, office) => {
    const officeRows = rows.filter((r) => r.Office === office);
    acc[office] = {
      count: officeRows.reduce((sum, r) => sum + safeNumber(r.ClientCount), 0),
      amount: officeRows.reduce(
        (sum, r) => sum + safeCurrencyNumber(r.ForecastAmount),
        0
      ),
    };
    return acc;
  }, {});

  // Sort offices by forecast amount, then count
  const totals = offices
    .map((o) => ({
      o,
      totalCount: officeTotalsMap[o]?.count ?? 0,
      totalAmount: officeTotalsMap[o]?.amount ?? 0,
    }))
    .sort((a, b) => {
      if (b.totalAmount !== a.totalAmount) return b.totalAmount - a.totalAmount;
      return b.totalCount - a.totalCount;
    })
    .map((x) => x.o);

  // Get unique client types
  const clientTypes = Array.from(new Set(rows.map((r) => r.ClientType))).filter(Boolean);

  // Create stable theme-based colors
  const colorMap = getPersistentPaletteMap(
    "client-dashboard.shared-client-type.colors",
    clientTypes,
    theme?.palette
  );

  // Helper to get one row
  const getRow = (office: string, clientType: string) =>
    rows.find(
      (r) => r.Office === office && r.ClientType === clientType
    );

  // Build one series per client type
  const series: ColumnSeries[] = clientTypes.map((clientType) => ({
    type: "column",
    name: clientType,
    color: colorMap[clientType],
    data: totals.map((office) => {
      const row = getRow(office, clientType);
      return {
        y: safeCurrencyNumber(row?.ForecastAmount),
        custom: {
          count: safeNumber(row?.ClientCount),
          forecastAmount: safeCurrencyNumber(row?.ForecastAmount),
          clientType,
          officeName: office,
        },
      };
    }),
  }));

  const setVisibleYAxisExtremes = (chart: Highcharts.Chart) => {
    try {
      const categoryAxis = chart.xAxis[0];
      const valueAxis = chart.yAxis[0];

      const minIndex = Math.max(0, Math.floor(Number(categoryAxis.min ?? 0)));
      const maxIndex = Math.min(
        Math.max(totals.length - 1, 0),
        Math.ceil(Number(categoryAxis.max ?? totals.length - 1))
      );

      let visibleMax = 0;

      for (let i = minIndex; i <= maxIndex; i++) {
        let stackTotal = 0;

        chart.series.forEach((series: any) => {
          if (series.visible) {
            const point = series.points?.[i];
            stackTotal += Number(point?.y ?? 0);
          }
        });

        visibleMax = Math.max(visibleMax, stackTotal);
      }

      const paddedMax = visibleMax > 0 ? visibleMax * 1.22 : 1;
      valueAxis.setExtremes(0, paddedMax, false, false);
      chart.redraw(false);
    } catch (e) {
      // no-op
    }
  };

  // Return Highcharts stacked column config
  return {
    ...chartDefaults,
    chart: {
      type: "column",
      spacingBottom: 20,
      marginRight: 80,
      events: {
        load: function () {
          setVisibleYAxisExtremes(this);
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: totals.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundExecutiveOffice",
          });
        },
      },
    },
    title: { text: "Expected Revenue by Office" },
    xAxis: {
      categories: totals,
      crosshair: true,
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      title: { text: "Office" },
      min: 0,
      max: Math.min(4, Math.max(totals.length - 1, 0)),
      scrollbar: buildStandardScrollbar({
        enabled: totals.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
      events: {
        afterSetExtremes: function () {
          setVisibleYAxisExtremes(this.chart);
        },
      },
    },
    yAxis: {
      min: 0,
      maxPadding: 0.12,
      title: { text: "Revenue Amount ($)" },
      labels: {
        formatter: function () {
          return formatChartCurrencyCompact(Number(this.value));
        },
      },
      stackLabels: {
        enabled: true,
        useHTML: true,
        y: -9,
        formatter: function () {
          const office = totals[this.x as number];
          const total = officeTotalsMap[office];

          if (!total) {
            return `${formatChartCurrencyCompact(Number(this.total ?? 0))}`;
          }

          return `
            <div style="
              text-align: center;
              background: #ffffff;
              padding: 4px 8px;
              border-radius: 6px;
              min-width: 78px;
            ">
              <div style="font-size: 11px; font-weight: 600; margin-bottom: 2px;">
                ${formatChartCurrencyCompact(Number(this.total ?? 0))}
              </div>
              <div style="font-size: 10px; color: #6b7280;">
                ${safeNumber(total.count)} clients
              </div>
            </div>
          `;
        },
      },
      gridLineWidth: 0,
      lineWidth: 0,
      reversedStacks: false,
    },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const office = String(this.x ?? "");
          const amount = Number(point?.custom?.forecastAmount ?? 0);
          const clientType = String(point?.custom?.clientType ?? this.series.name ?? "");
          const count = Number(point?.custom?.count ?? 0);

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${office}</div>
              <div>${clientType} Revenue: <b>${formatChartCurrencyCompact(amount)}</b></div>
              <div>${clientType} Clients: <b>${count}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 28,
        pointPadding: 0.18,
        groupPadding: 0.2,
        events: {
          legendItemClick: function () {
            const chart = this.chart;
            setTimeout(() => setVisibleYAxisExtremes(chart), 0);
          },
        },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
    },
    series,
  } satisfies Highcharts.Options;
};